import { useState } from 'react'
import { Modal } from './Modal'
import type { Person, Week } from '@shared/types'

interface Props {
  week: Week
  currentUser: string
  people: Person[]
  /** Opens the composer; sending happens from there. */
  onCompose: () => void
  onApprove: (comment: string) => void
  onReopen: () => void
  busy: boolean
}

const when = (stamp: string | null) => (stamp ? stamp.slice(0, 10) : '')

/**
 * The handoff: one parent builds the week, the other looks it over.
 *
 * It sits inline in the week bar, next to the notes and width controls, rather
 * than in a strip of its own. Most weeks never get sent, so the usual state is
 * a single button; the states that carry news -- in review, approved -- earn a
 * pill beside it, with the who and when in its tooltip.
 */
export function ReviewControl({
  week,
  currentUser,
  people,
  onCompose,
  onApprove,
  onReopen,
  busy,
}: Props) {
  const [commenting, setCommenting] = useState(false)
  const [comment, setComment] = useState('')

  const me = currentUser.toLowerCase()
  const mine = week.reviewRequestedBy.toLowerCase() === me
  const packed = week.lunches.some((l) => l.items.length > 0)

  // Who a request would actually reach: opted in, has an address, isn't me.
  const reviewers = people.filter((p) => p.notifyReview && p.name.toLowerCase() !== me)

  if (week.reviewState === 'approved') {
    const detail = `${week.reviewedBy || 'Someone'}${week.reviewedAt ? ` · ${when(week.reviewedAt)}` : ''}`
    return (
      <div className="review-control">
        <span className="review-pill approved" title={`Approved by ${detail}`}>
          ✓ Approved
        </span>
        <button className="btn sm ghost" onClick={onReopen} disabled={busy}>
          Keep editing
        </button>
      </div>
    )
  }

  if (week.reviewState === 'in_review') {
    const detail = mine
      ? `You sent this${week.reviewRequestedAt ? ` on ${when(week.reviewRequestedAt)}` : ''}`
      : `${week.reviewRequestedBy || 'Someone'} sent this to you`
    return (
      <div className="review-control">
        <span className="review-pill in-review" title={detail}>
          ◷ In review
        </span>
        <button className="btn sm ghost" onClick={onReopen} disabled={busy}>
          {mine ? 'Take it back' : 'Needs changes'}
        </button>
        {!mine && (
          <button className="btn sm primary" onClick={() => setCommenting(true)} disabled={busy}>
            Looks good
          </button>
        )}

        {commenting && (
          <Modal
            title="Approve this week"
            onClose={() => setCommenting(false)}
            footer={
              <>
                <button className="btn ghost" onClick={() => setCommenting(false)} disabled={busy}>
                  Cancel
                </button>
                <button
                  className="btn primary"
                  onClick={() => onApprove(comment.trim())}
                  disabled={busy}
                >
                  Approve
                </button>
              </>
            }
          >
            <p className="muted" style={{ marginTop: 0 }}>
              {detail}. Approving lets {week.reviewRequestedBy || 'them'} know the week is good to
              pack.
            </p>
            <input
              type="text"
              autoComplete="off"
              placeholder="Anything to say? (optional)"
              value={comment}
              maxLength={500}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onApprove(comment.trim())
              }}
              aria-label="Comment on approval"
              autoFocus
            />
          </Modal>
        )}
      </div>
    )
  }

  // Planning. The explanation of who it reaches would be a sentence of chrome
  // on every week, so it rides the tooltip -- and the composer says it again,
  // with addresses, before anything is sent.
  const hint = !packed
    ? 'Fill some days in first, then send the week over for a look.'
    : reviewers.length > 0
      ? `Sends to ${reviewers.map((p) => p.name).join(' and ')}. You get to write a message first.`
      : 'Nobody has review emails on yet — add an address in Settings.'

  return (
    <div className="review-control">
      <button
        className="btn sm"
        onClick={onCompose}
        disabled={busy || !packed}
        title={hint}
      >
        📩 Send for review…
      </button>
    </div>
  )
}
